import React, { useState } from "react";
import { Dialog, DialogContent, TextField, Box } from "@mui/material";
import { IconClose } from "../../Icons/Icons";
import { colors, transitions } from "../../../styles/theme";
import { Description } from "../components/Description/Description";
import { Title } from "../components/TItle/Title";
import { TextInput } from "../components/TextInput/TextInput";
import { LabeledCheckbox } from "../components/LabeledCheckbox/LabeledCheckbox";
import { SubmitButton } from "../components/SubmitButton/SubmitButton";
import { BasicLink } from "../components/BasicLink/BasicLink";
import { GradientFrame } from "../../GradientFrame/GradientFrame";
import { useForm } from "../hooks";
import { FormProps } from "../types";
import { useResponsiveScaler } from "../../../lib/ResponsiveScaler";
import { PhoneNumberInput } from "../components/PhoneNumberInput/PhoneNumberInput";

export const ContactForm: React.FC<FormProps> = (props) => {
  const { open, onClose, onSubmit, disabled } = props;
  const { values, errors, handleChange, handleBlur, handleSubmit, showError } =
    useForm({ onSubmit });
  const { rpx } = useResponsiveScaler();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent>
        <GradientFrame $type={"top"}>
          <Box
            sx={{
              display: "flex",
              background: colors.background.v1,
              flexDirection: "column",
              borderRadius: "15px",
              ...rpx({
                padding: "60px",
              }),
            }}
          >
            <Box
              sx={{
                ...rpx(
                  {
                    marginBottom: "50px",
                  },
                  {
                    mobile: {
                      marginBottom: "10px",
                    },
                  },
                ),
              }}
            >
              <Title>Contact Us</Title>
              <Description>
                For more personalized configurations, our sales team will be
                happy to assist you.
              </Description>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: "stretch",
                gap: "26px",
                ...rpx(
                  {},
                  {
                    mobile: {
                      gap: "10px",
                    },
                  },
                ),
              }}
            >
              {/* Left column */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flex: { xs: "1 1 auto", md: "0 0 40%" },
                  gap: "26px",
                  ...rpx(
                    {},
                    {
                      mobile: {
                        gap: "10px",
                      },
                    },
                  ),
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    columnGap: "26px",
                    ...rpx(
                      {},
                      {
                        mobile: {
                          columnGap: "10px",
                        },
                      },
                    ),
                  }}
                >
                  <TextInput
                    containerProps={{ width: "50%" }}
                    label="First name*"
                    variant="outlined"
                    fullWidth
                    value={values.first_name}
                    // @ts-ignore
                    onChange={handleChange("first_name")}
                    onBlur={handleBlur("first_name")}
                    error={showError("first_name")}
                    helperText={
                      showError("first_name") ? errors.first_name : " "
                    }
                  />
                  <TextInput
                    containerProps={{ width: "50%" }}
                    label="Last name*"
                    variant="outlined"
                    fullWidth
                    value={values.last_name}
                    // @ts-ignore
                    onChange={handleChange("last_name")}
                    onBlur={handleBlur("last_name")}
                    error={showError("last_name")}
                    helperText={showError("last_name") ? errors.last_name : " "}
                  />
                </Box>
                {/*<Box sx={{ display: "flex", flexDirection: "column", gap: "26px" }}>*/}
                <TextInput
                  // containerProps={{ sx: { flexGrow: 2 } }}
                  label="Email*"
                  variant="outlined"
                  fullWidth
                  value={values.email}
                  // @ts-ignore
                  onChange={handleChange("email")}
                  onBlur={handleBlur("email")}
                  error={showError("email")}
                  helperText={showError("email") ? errors.email : " "}
                />
                <PhoneNumberInput
                  value={values.phone}
                  onChange={(val) =>
                    handleChange("phone")({ target: { value: val } })
                  }
                  error={showError("phone")}
                  helperText={showError("phone") ? errors.phone : " "}
                />
                {/*</Box>*/}
              </Box>

              {/* Right column */}
              <Box
                sx={{
                  display: "flex",
                  flex: { xs: "1 1 auto", md: "1 0 auto" },
                }}
              >
                <TextField
                  label="Message (optional)"
                  multiline
                  rows="auto"
                  fullWidth
                  name="message"
                  value={values.message ?? ""}
                  onChange={handleChange("message")}
                  onBlur={handleBlur("message")}
                  sx={{
                    flex: 1,
                    "& .MuiInputBase-root": {
                      height: "100%",
                      alignItems: "stretch",
                    },
                    "& .MuiInputBase-inputMultiline": { height: "100%" },
                  }}
                />
              </Box>
            </Box>

            <Box>
              <Box
                sx={{
                  marginTop: "30px",
                  ...rpx(
                    {},
                    {
                      mobile: { marginTop: "10px" },
                    },
                  ),
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: {
                      xxl: "center",
                    },
                    flexDirection: {
                      xs: "column",
                      xxl: "row",
                    },
                    // While stacked, the privacy error is absolutely positioned
                    // and would overlap the newsletter checkbox below — open room
                    // for it only when it shows (no change to the valid state).
                    rowGap: showError("accepted")
                      ? { xs: "28px", xxl: 0 }
                      : undefined,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: {
                        xs: "space-between",
                        xxl: "auto",
                      },
                    }}
                  >
                    <LabeledCheckbox
                      value={values.accepted}
                      name={"accepted"}
                      text={"Accept Privacy Policy*"}
                      onChange={handleChange("accepted")}
                      onBlur={handleBlur("accepted")}
                      error={showError("accepted")}
                    />
                    <BasicLink
                      href={"https://saycarbonyachts.com/privacy-policy"}
                      target={"_blank"}
                      typographyProps={{
                        variant: "v2",
                      }}
                      sx={{
                        marginLeft: {
                          xs: "10px",
                          md: "15px",
                          xxxl: "36px",
                        },
                      }}
                    >
                      View Privacy Policy
                    </BasicLink>
                  </Box>

                  <LabeledCheckbox
                    value={values.signed_up}
                    name={"signed_up"}
                    text={"Sign up for SAY Carbon Yachts Newsletter."}
                    onChange={handleChange("signed_up")}
                    onBlur={handleBlur("signed_up")}
                    sx={{
                      marginLeft: {
                        xxl: "200px",
                      },
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: "55px",
                  }}
                >
                  <SubmitButton disabled={disabled} onClick={handleSubmit}>
                    Send Request
                  </SubmitButton>
                  <IconClose
                    sx={{
                      color: colors.text.v2,
                      cursor: "pointer",
                      alignSelf: "end",
                      transition: transitions.join("color"),
                      "&:hover": { color: colors.text.v3 },
                    }}
                    onClick={onClose}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </GradientFrame>
      </DialogContent>
    </Dialog>
  );
};
